// ─── Website content ───────────────────────────────────────────────────────
// Everything the public website says lives here, so it can be edited in one
// place without touching layout code.

export const CLINIC = {
  name: "Sai Heal n Walk Physiotherapy Clinic",
  shortName: "Sai Heal n Walk",
  doctor: "Dr. Yuganta Mhatre Angre",
  doctorShort: "Dr. Yuganta",
  phone: "9970534427",
  phoneDisplay: "+91 99705 34427",
  email: "dryuga.hnw@gmail.com",
  whatsapp: "919970534427",
  address: ["Vidyanagar, Off Mogalwadi Road", "Khopoli 410203, Maharashtra"],
  addressOneLine: "Vidyanagar, Off Mogalwadi Road, Khopoli 410203, Maharashtra",
  landmark: "Opposite Tembe Swami Math",
  lat: 18.781258,
  lng: 73.343666,
  mapUrl: "https://maps.google.com/?q=18.781258,73.343666",
  directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=18.781258,73.343666",
  mapEmbed: "https://maps.google.com/maps?q=18.781258,73.343666&z=16&output=embed",
  instagram: "https://www.instagram.com/dr_yuganta?utm_source=qr",
  facebook: "https://www.facebook.com/share/19VDFMcfpV/?mibextid=wwXIfr",
  tagline: "Cos life is about living, not just surviving.",
};

// Clinic hours (minutes from midnight). Sunday closed.
export const HOURS = [
  { label: "Morning", from: 10 * 60, to: 14 * 60, text: "10:00 AM – 2:00 PM" },
  { label: "Evening", from: 17 * 60, to: 20 * 60 + 30, text: "5:00 PM – 8:30 PM" },
];
export const CLOSED_DAYS = [0];
export const WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const telLink = `tel:+91${CLINIC.phone}`;
export const waLink = (text = "Hello Dr. Yuganta, I would like to book a physiotherapy appointment.") =>
  `https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent(text)}`;

export const CREDENTIALS = [
  { icon: "bi-mortarboard", title: "BPT", text: "Sancheti Institute for Orthopaedics and Rehabilitation, Pune" },
  { icon: "bi-award", title: "MPT", text: "Dr. D. Y. Patil University, Pune" },
  { icon: "bi-hospital", title: "Ex-Consultant", text: "Dhirubhai Ambani Hospital, Lodhivali" },
  { icon: "bi-people", title: "Kid to adult care", text: "Treatment for every age group" },
];

export const ABOUT_SHORT = [
  "Sai Heal n Walk Physiotherapy Clinic, Khopoli is a patient-centred physiotherapy clinic dedicated to helping individuals reduce pain, restore movement, regain strength and return to an active, independent lifestyle.",
  "Under the guidance of Dr. Yuganta Mhatre Angre, we provide personalised physiotherapy and rehabilitation programs based on each patient’s condition, goals and functional needs.",
  "Our approach focuses not just on temporary pain relief, but on understanding the underlying problem, improving mobility and strength, preventing recurrence and helping patients move with confidence.",
];

export const ABOUT_LONG = [
  "Sai Heal n Walk Physiotherapy Clinic, Khopoli is a patient-centred physiotherapy and rehabilitation clinic committed to helping people overcome pain, restore movement, regain strength, and return to a more active and independent lifestyle.",
  "Under the guidance of Dr. Yuganta Mhatre Angre, our clinic provides personalised physiotherapy and rehabilitation programs designed according to each patient’s condition, physical abilities, lifestyle, and individual recovery goals.",
  "We understand that every patient is different. The same pain or movement problem can have different causes, and therefore treatment should never be a one-size-fits-all approach. Our assessment focuses on understanding the patient’s symptoms, movement limitations, strength, posture, functional difficulties, and the factors contributing to the problem.",
];

export const APPROACH_INTRO =
  "At Sai Heal n Walk, our goal is not simply to provide temporary pain relief. We aim to identify and address the underlying factors contributing to pain, weakness, stiffness, restricted movement, or reduced function.";

export const APPROACH = [
  { icon: "bi-clipboard2-pulse", text: "Detailed physiotherapy assessment" },
  { icon: "bi-person-arms-up", text: "Individualised exercise programs" },
  { icon: "bi-heart-pulse", text: "Pain management" },
  { icon: "bi-hand-index-thumb", text: "Manual therapy and mobilisation techniques" },
  { icon: "bi-lightning-charge", text: "Strengthening and flexibility exercises" },
  { icon: "bi-arrows-vertical", text: "Posture and movement correction" },
  { icon: "bi-bullseye", text: "Balance and coordination training" },
  { icon: "bi-house-check", text: "Functional rehabilitation" },
  { icon: "bi-trophy", text: "Sports injury rehabilitation" },
  { icon: "bi-diagram-3", text: "Neurological rehabilitation" },
  { icon: "bi-house-heart", text: "Home exercise programs" },
  { icon: "bi-shield-check", text: "Guidance for preventing recurrent injuries" },
];
export const APPROACH_OUTRO =
  "Treatment is progressed according to the patient’s recovery, with regular monitoring and modifications when required.";

// Recovery as a sequence — Heal. Move. Walk.
export const JOURNEY = [
  {
    word: "Heal",
    title: "Understand the problem",
    text: "A detailed assessment of your symptoms, movement, strength and posture, so we treat the cause and not just the pain.",
  },
  {
    word: "Move",
    title: "Restore movement and strength",
    text: "Hands-on therapy and a personalised exercise program, progressed as you improve and adjusted when needed.",
  },
  {
    word: "Walk",
    title: "Return to the life you enjoy",
    text: "Back to work, sport and everyday activities with confidence — with guidance to help prevent the problem coming back.",
  },
];

// Conditions from the clinic's printed list (English + Marathi), grouped.
export const CONDITION_GROUPS = [
  {
    key: "pain",
    label: "Pain & joints",
    icon: "bi-activity",
    items: [
      { en: "Low back pain (with / without leg pain)", mr: "कंबर दुखी व पायात मुंग्या येणे" },
      { en: "Neck pain (with / without hand pain)", mr: "मान दुखी व हातात मुंग्या येणे" },
      { en: "Joint pain", mr: "सांधेदुखी" },
      { en: "Muscle pain / Tendon pain", mr: "स्नायूमध्ये दुखणे" },
      { en: "Osteoarthritis / Rheumatoid arthritis", mr: "संधीवाताचा त्रास" },
      { en: "Frozen shoulder / Muscle injury to shoulder", mr: "खांदे दुखी" },
      { en: "Spondylosis", mr: "स्पॉन्डीलिसीस" },
      { en: "Stiffness / Trigger points", mr: "स्नायूमध्ये कडकपणा / ट्रिगर पॉइंट" },
      { en: "Tennis elbow" },
    ],
  },
  {
    key: "injury",
    label: "Injury, sports & surgery",
    icon: "bi-bandaid",
    items: [
      { en: "Fractures (post-operative / post-cast) management" },
      { en: "Post-operative management", mr: "ऑपरेशन नंतर" },
      { en: "Hip / Knee replacement" },
      { en: "Sports injuries, sprains and muscle strains" },
      { en: "Ligament and tendon injuries" },
      { en: "Overuse injuries and return-to-sport rehabilitation" },
    ],
  },
  {
    key: "neuro",
    label: "Brain & nerves",
    icon: "bi-diagram-3",
    items: [
      { en: "Stroke", mr: "पॅरालिसीस" },
      { en: "Weakness / Paralysis", mr: "कमजोरी / पॅरालिसीस" },
      { en: "Facial paralysis / Bell’s palsy" },
      { en: "Tetraplegia, paraplegia" },
      { en: "Balance problems" },
    ],
  },
  {
    key: "children",
    label: "Children",
    icon: "bi-balloon-heart",
    items: [
      { en: "Cerebral palsy", mr: "सेरेब्रल पालसी" },
      { en: "Delayed milestones" },
    ],
  },
  {
    key: "life",
    label: "Women, seniors & lifestyle",
    icon: "bi-person-heart",
    items: [
      { en: "Pre & post natal care", mr: "प्रेग्नेन्सी आधी व नंतर" },
      { en: "Geriatric care / Old age problems" },
      { en: "Obesity / Overweight management", mr: "वजन कमी होणे" },
      { en: "Postural problems" },
      { en: "Occupational disorders" },
      { en: "Cardiac & respiratory care" },
    ],
  },
];

export const ALL_CONDITIONS = CONDITION_GROUPS.flatMap((g) => g.items.map((i) => i.en));

export const WHY = [
  { icon: "bi-person-check", title: "Patient-centred care", text: "Every treatment plan is tailored to the patient’s specific condition, needs, and goals." },
  { icon: "bi-chat-heart", title: "Individual attention", text: "We believe patients should receive personalised guidance throughout their rehabilitation journey." },
  { icon: "bi-graph-up-arrow", title: "Focus on long-term recovery", text: "Our aim is to address the factors contributing to the problem and reduce the chances of recurrence." },
  { icon: "bi-house-heart", title: "Functional rehabilitation", text: "We focus on helping patients return to meaningful daily activities, work, exercise, and an active lifestyle." },
  { icon: "bi-book", title: "Education & empowerment", text: "Patients are guided about exercises, posture, movement, and lifestyle modifications so they can take an active role in their recovery." },
];

export const DOCTOR = {
  intro:
    "Dr. Yuganta Mhatre Angre is a qualified physiotherapist dedicated to providing personalised physiotherapy and rehabilitation care to patients of all age groups.",
  education: [
    { degree: "Bachelor of Physiotherapy (BPT)", place: "Sancheti Institute for Orthopaedics and Rehabilitation, Pune" },
    { degree: "Master of Physiotherapy (MPT)", place: "Dr. D. Y. Patil University, Pune" },
    { degree: "Ex-Consultant", place: "Dhirubhai Ambani Hospital, Lodhivali" },
  ],
  background:
    "Her academic background, combined with clinical experience, enables her to assess each patient individually and develop treatment and rehabilitation programs based on their specific condition, functional requirements, and recovery goals.",
  approach: [
    "Dr. Yuganta Mhatre Angre believes that effective physiotherapy involves more than simply treating pain. Her approach focuses on identifying the underlying cause of the problem, improving strength and mobility, restoring functional independence, and educating patients about appropriate exercises and preventive strategies.",
    "At Sai Heal n Walk Physiotherapy Clinic, Khopoli, she is committed to providing compassionate, patient-focused care and helping every individual progress towards a healthier, more active, and independent lifestyle.",
  ],
};

export const PHILOSOPHY = [
  "At Sai Heal n Walk Physiotherapy Clinic, we believe physiotherapy is not just about reducing pain. It is about helping people regain movement, independence, confidence, and the ability to enjoy the things that make life meaningful.",
  "Our goal is to help every patient move better, live better, and get back to doing what they love.",
];

export const PERSONALISED = [
  "We believe successful physiotherapy is not only about reducing symptoms but also about helping patients regain their ability to perform everyday activities comfortably and confidently.",
  "Whether the goal is to walk independently, return to work, resume sports, perform daily activities without pain, or regain strength after an injury or illness, we develop treatment and exercise programs according to the individual’s functional goals.",
  "We also educate patients about proper movement, posture, exercise techniques, activity modification, and preventive strategies so they can actively participate in their own recovery.",
];

export const MISSION = [
  "Our mission is to make quality physiotherapy and rehabilitation accessible to the people of Khopoli and surrounding areas, while providing compassionate, personalised, and goal-oriented care.",
  "We strive to create a comfortable environment where patients feel heard, understood, and supported throughout their recovery.",
];

export const RECOVERY_JOURNEY = [
  "We believe recovery is a journey. With the right assessment, appropriate treatment, consistent exercise, and patient participation, individuals can make meaningful improvements in their movement, strength, confidence, and quality of life.",
  "At Sai Heal n Walk Physiotherapy Clinic, we are committed to walking alongside our patients throughout that journey — from managing pain to restoring movement and helping them return to the activities they love.",
];

export const FAQ = [
  {
    q: "Do I need an appointment?",
    a: "Prior appointment is preferred so we can give you dedicated time. You can book online, call, or WhatsApp us on 99705 34427.",
  },
  {
    q: "What should I bring?",
    a: "Please bring any reports, X-rays, MRI scans, prescriptions or a surgeon’s notes related to your problem, and wear comfortable clothing.",
  },
  {
    q: "What happens at the first visit?",
    a: "A detailed assessment of your symptoms, movement, strength and posture, followed by a treatment plan built around your goals.",
  },
  {
    q: "Do you treat children?",
    a: "Yes — from children with cerebral palsy or delayed milestones to adults and seniors. We provide kid to adult care.",
  },
  {
    q: "When is the clinic open?",
    a: "Monday to Saturday, 10:00 AM – 2:00 PM and 5:00 PM – 8:30 PM. The clinic is closed on Sundays.",
  },
];
